import { FloatingPortal } from '@floating-ui/react';
import React, { RefObject, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Vector2 } from 'three';

import { GcpModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { isDefined } from '../../../../utils/variables';
import { mouseMovementTreshold } from '../../../../viewer/types';
import { useFloatingPosition } from '../../../hooks/use-floating-position';
import { usePointerEvent } from '../../../hooks/use-pointer-up-event';
import { Icon } from '../../icon/icon';
import { Stack } from '../../stack/stack';
import { GeodataContext } from '../geodata-state';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';
import { sfmViewerLayering } from './layering';
import { GeodataImageItemWithMarkers, PlaceMarkersContext } from './place-markers-state';
import { computeObservationVector, selectDiverseEstimates } from './select-diverse-estimates';
import { CursorIntersectionResult } from './use-cursor';

export type SfmViewerTool = 'select' | 'measure';

type Props = {
  intersection?: CursorIntersectionResult;
  camerasObjectUuid?: string;
  gcpObjectUuid?: string;
  pointerRef?: RefObject<HTMLDivElement>;
};

export const SfmViewerTooltip = ({
  intersection,
  camerasObjectUuid,
  gcpObjectUuid,
  pointerRef,
}: Props) => {
  const { t } = useTranslation();
  const { signer, geodata } = useContext(GeodataContext);
  const { origo, getImageLocalPosition } = useContext(GeodataSpatialReferenceContext);
  const {
    selectImage,
    selectGcp,
    setShowPlaceMarkersDialog,
    setSortedImageList,
    imagesWithMarkers,
    gcp,
  } = useContext(PlaceMarkersContext);
  const [hoveredImage, setHoveredImage] = useState<{
    image: GeodataImageItemWithMarkers;
    thumbnailUrl: string;
  }>();
  const [savedIntersection, setSavedIntersection] = useState<CursorIntersectionResult>();
  const [hoveredGcp, setHoveredGcp] = useState<GcpModel>();
  const [lastMousePosition, setLastMousePosition] = useState<Vector2>(new Vector2());
  const [isMouseInContainer, setIsMouseInContainer] = useState(false);

  useEffect(() => {
    if (!pointerRef?.current) {
      return;
    }
    const container = pointerRef.current;
    const isHovering = isDefined(hoveredGcp) || isDefined(hoveredImage);
    container.style.cursor = isHovering ? 'pointer' : '';
    return () => {
      container.style.cursor = '';
    };
  }, [hoveredGcp, hoveredImage, pointerRef]);

  const {
    ref: floatingRef,
    floatingStyles,
    floatingProps,
  } = useFloatingPosition({
    followMousePointer: true,
    middleware: 'shift',
    offset: {
      mousePointer: 10,
      viewportEdge: 5,
    },
    startPos: intersection?.mousePosition.client
      ? {
          x: intersection.mousePosition.client.x,
          y: intersection.mousePosition.client.y,
        }
      : undefined,
    strategy: 'fixed',
  });

  useEffect(() => {
    if (
      !isDefined(intersection) ||
      !isDefined(signer) ||
      !isDefined(intersection.intersection.index)
    ) {
      setHoveredImage(undefined);
      setHoveredGcp(undefined);
      return;
    }

    if (intersection.intersection.object.uuid === camerasObjectUuid) {
      const image = imagesWithMarkers[intersection.intersection.index];

      if (image) {
        setHoveredImage({
          image,
          thumbnailUrl: signer.getSignedRequest(`thumbnails/${image.imageIdentifier}`),
        });
      }
    } else {
      setHoveredImage(undefined);
    }

    if (
      isDefined(geodata.gcpCollection) &&
      intersection.intersection.object.uuid === gcpObjectUuid
    ) {
      const gcp = geodata.gcpCollection.points[intersection.intersection.index];
      setHoveredGcp(gcp);
    } else {
      setHoveredGcp(undefined);
    }
  }, [intersection, camerasObjectUuid, gcpObjectUuid, imagesWithMarkers, signer, geodata]);

  useEffect(() => {
    if (!pointerRef?.current) {
      return;
    }

    const handleMouseEnter = () => {
      setIsMouseInContainer(true);
    };

    const handleMouseLeave = () => {
      setIsMouseInContainer(false);
    };

    const container = pointerRef.current;
    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [pointerRef]);

  const onLeftUp = useCallback(
    (mouseEvent: MouseEvent) => {
      if (
        new Vector2(mouseEvent.clientX, mouseEvent.clientY).distanceTo(lastMousePosition) >
        mouseMovementTreshold
      ) {
        return;
      }

      // Select tool behavior
      if (
        isDefined(hoveredImage) &&
        isDefined(hoveredImage.image.transform) &&
        isDefined(getImageLocalPosition) &&
        hoveredImage.image.status !== 'UPLOADING' &&
        hoveredImage.image.status !== 'PENDING'
      ) {
        const clickedHasGcpMarker =
          isDefined(gcp) &&
          isDefined(gcp.id) &&
          hoveredImage.image.markers.some((m) => m.gcpId === gcp.id);

        if (
          isDefined(gcp) &&
          isDefined(gcp.id) &&
          isDefined(origo) &&
          clickedHasGcpMarker &&
          isDefined(getImageLocalPosition)
        ) {
          const gcpLocal: [number, number, number] = [
            gcp.enu[0] - origo.x,
            gcp.enu[1] - origo.y,
            gcp.enu[2] - origo.z,
          ];

          const candidates = imagesWithMarkers.filter(
            (x) =>
              isDefined(x.imageId) &&
              isDefined(x.transform) &&
              x.markers.some((m) => m.gcpId === gcp.id),
          );

          const getCamVec3 = (img: GeodataImageItemWithMarkers): [number, number, number] => {
            const pos = getImageLocalPosition(img.transform!);
            return [pos.x, pos.y, pos.z];
          };

          const isCommitted = (img: GeodataImageItemWithMarkers): boolean => {
            const marker = img.markers.find((m) => m.gcpId === gcp.id);
            return (
              marker?.pinned === true ||
              (marker?.estimates?.some((e) => e.status === 'ACCEPTED') ?? false)
            );
          };

          const committedCandidates = candidates.filter(isCommitted);
          const uncommittedCandidates = candidates.filter((x) => !isCommitted(x));

          const clickedImageId = hoveredImage.image.imageId;

          const pinnedSeeds = [...committedCandidates, hoveredImage.image].map((img) =>
            computeObservationVector(getCamVec3(img), gcpLocal),
          );
          const sorted = [
            hoveredImage.image,
            ...selectDiverseEstimates(
              uncommittedCandidates.filter((x) => x.imageId !== clickedImageId),
              getCamVec3,
              () => gcpLocal,
              () => 1,
              Infinity,
              0,
              pinnedSeeds,
            ),
            ...committedCandidates.filter((x) => x.imageId !== clickedImageId),
          ];

          setSortedImageList(sorted.map((x) => ({ imageId: x.imageId! })));
        } else {
          setSortedImageList(
            imagesWithMarkers
              .filter((x) => isDefined(x.imageId) && isDefined(x.transform))
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map((x) => ({ imageId: x.imageId! })),
          );
        }
        selectImage(hoveredImage.image);
        setShowPlaceMarkersDialog(true);
        setSavedIntersection(undefined);
      } else if (isDefined(hoveredGcp)) {
        selectGcp(hoveredGcp);
      }
    },
    [
      setShowPlaceMarkersDialog,
      setSortedImageList,
      imagesWithMarkers,
      lastMousePosition,
      selectImage,
      selectGcp,
      hoveredImage,
      hoveredGcp,
      getImageLocalPosition,
      gcp,
      origo,
    ],
  );

  const onRightClick = useCallback(() => {
    // Select tool behavior: toggle saved intersection
    if (isDefined(savedIntersection)) {
      setSavedIntersection(undefined);
    } else {
      setSavedIntersection(intersection);
    }
  }, [savedIntersection, intersection]);

  const onLeftDown = useCallback(
    (mouseEvent: MouseEvent) => {
      setLastMousePosition(new Vector2(mouseEvent.clientX, mouseEvent.clientY));
    },
    [setLastMousePosition],
  );

  usePointerEvent('pointerdown', pointerRef, 'left', onLeftDown);
  usePointerEvent('pointerup', pointerRef, 'left', onLeftUp);
  usePointerEvent('pointerup', pointerRef, 'right', onRightClick);

  // Only show tooltips when intersection is available and mouse is in container
  const isVisible = useMemo(() => {
    return (
      isDefined(intersection) &&
      isDefined(signer) &&
      isDefined(intersection.intersection.index) &&
      isMouseInContainer
    );
  }, [intersection, signer, isMouseInContainer]);

  const tooltipContent = useMemo(() => {
    return isVisible && isDefined(intersection) ? (
      <Stack spacing={0.2}>
        {hoveredImage && (
          <>
            <span>{hoveredImage.image.displayName}</span>
            {hoveredImage.image.status !== 'UPLOADING' &&
            hoveredImage.image.status !== 'PENDING' ? (
              <img src={hoveredImage.thumbnailUrl} />
            ) : (
              <Icon fixedWidth={true} icon={['fas', 'spinner']} spin={true} />
            )}
          </>
        )}
        {hoveredGcp && <span>{hoveredGcp.name}</span>}
        <span>N: {intersection.coordinates.northing.toFixed(3)}</span>
        <span>E: {intersection.coordinates.easting.toFixed(3)}</span>
        <span>H: {intersection.coordinates.height.toFixed(3)}</span>
        {hoveredImage && (
          <span>
            {t('aligner.table.standardDeviation', { ns: 'cloudProcessing' })}:{' '}
            {hoveredImage.image.totalStandardDeviation.toFixed(3)}m
          </span>
        )}
        {hoveredImage?.image.exifData && (
          <>
            <span>
              {t('legendsList.options.aperture', { ns: 'cloudProcessing' })}: f/
              {hoveredImage.image.exifData.apertureValue}
            </span>
            <span>ISO: {hoveredImage.image.exifData.iso}</span>
            <span>
              {t('legendsList.options.shutterSpeed', { ns: 'cloudProcessing' })}:{' '}
              {hoveredImage.image.exifData.exposureTime}
            </span>
          </>
        )}
      </Stack>
    ) : null;
  }, [intersection, hoveredImage, hoveredGcp, isVisible, t]);

  if (!tooltipContent) {
    return null;
  }

  return (
    <FloatingPortal>
      <Component ref={floatingRef} style={floatingStyles} {...floatingProps}>
        {tooltipContent}
      </Component>
    </FloatingPortal>
  );
};

const Component = styled.div`
  color: white;
  background-color: rgba(0, 0, 0, 0.72);
  border: 1px solid ${({ theme }) => theme.color.gray.darkest};
  padding: 0.5em;
  z-index: ${sfmViewerLayering.tooltip};
  pointer-events: none;
`;
